// src/pages/ObjectView.tsx
import React, { useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Breadcrumbs,
  Link,
  Divider,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  QrCode as QrCodeIcon,
  Nfc as NfcIcon,
  Assignment as AssignmentIcon,
  Assessment as AssessmentIcon,
  Info as InfoIcon,
  Business as BusinessIcon,
  Apartment as ApartmentIcon,
  Settings  as PrecisionIcon,
  Memory as MemoryIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import ObjectHierarchy from '../../components/ObjectHierarchy';
import { NodeDto } from '../../types/nodes';

// =============================================================================================
// ==== Dodane w Krok 2 >>>>
import { fetchNodeForms } from '../../store/slices/nodesSlice'; 
// <<<< Dodane w Krok 2 ====
// =============================================================================================


const ObjectView: React.FC = () => {
  const navigate = useNavigate();
  const { nodeId } = useParams<{ nodeId?: string }>();
  const { selectedNode, loading } = useAppSelector((state) => state.nodes);

  // =============================================================================================
  // ==== Dodane w Krok 2 >>>>
  const selectedNodeForms = useAppSelector((state) => state.nodes.selectedNodeForms);
  const formsLoading = useAppSelector((state) => state.nodes.formsLoading);
  // <<<< Dodane w Krok 2 ====
  // =============================================================================================


  const [selectedFromTree, setSelectedFromTree] = useState<NodeDto | null>(null);

  const currentNode = selectedNode || selectedFromTree;

  // =============================================================================================
  // ==== Dodane w Krok 2 >>>>
 
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    if (nodeId) {
      dispatch(fetchNodeForms(Number(nodeId)));
    }
  }, [nodeId, dispatch]);

  // <<<< Dodane w Krok 2 ====
  // =============================================================================================
 

  const handleNodeSelect = (node: NodeDto) => {
    setSelectedFromTree(node);
    navigate(`/objects/${node.nodeId}`);
  };

  const getNodeIcon = (level: number) => {
    const icons = [BusinessIcon, ApartmentIcon, PrecisionIcon, MemoryIcon];
    const IconComponent = icons[level] || MemoryIcon;
    return <IconComponent />;
  };

  const getNodeTypeLabel = (level: number) => {
    const labels = ['Organization', 'Hall', 'Line', 'Machine'];
    return labels[level] || 'Object';
  };

  const renderBreadcrumbs = () => {
    if (!currentNode?.path) return null;

    const pathParts = currentNode.path.split(' / ');
    
    return (
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 2 }}
      >
        <Link 
          color="inherit" 
          href="/objects" 
          onClick={(e) => { e.preventDefault(); navigate('/objects'); }}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <BusinessIcon sx={{ mr: 0.5, fontSize: 16 }} />
          Objects
        </Link>
        {pathParts.map((part, index) => (
          <Typography 
            key={index} 
            color={index === pathParts.length - 1 ? 'text.primary' : 'inherit'}
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              fontWeight: index === pathParts.length - 1 ? 'medium' : 'normal'
            }}
          >
            {index === pathParts.length - 1 && getNodeIcon(currentNode.level)}
            {part}
          </Typography>
        ))}
      </Breadcrumbs>
    );
  };

  const renderNodeDetail = () => {
    if (!currentNode) {
      return (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Select an Object
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose an object from the hierarchy to view its details and available actions.
            </Typography>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{ color: 'primary.main', mr: 1 }}>
              {getNodeIcon(currentNode.level)}
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h5" component="h1">
                {currentNode.name}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                {getNodeTypeLabel(currentNode.level)} • Level {currentNode.level}
              </Typography>
            </Box>
            {currentNode.externalId && (
              <Chip 
                label={`ID: ${currentNode.externalId}`}
                variant="outlined"
                size="small"
              />
            )}
          </Box>

          {currentNode.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {currentNode.description}
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          {/* QR/NFC Codes */}
          {(currentNode.barCodeId || currentNode.nfcId) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Access Codes
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {currentNode.barCodeId && (
                  <Chip
                    icon={<QrCodeIcon />}
                    label={`QR: ${currentNode.barCodeId}`}
                    variant="outlined"
                    color="primary"
                  />
                )}
                {currentNode.nfcId && (
                  <Chip
                    icon={<NfcIcon />}
                    label={`NFC: ${currentNode.nfcId}`}
                    variant="outlined"
                    color="secondary"
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Object Information */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Object Information
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, fontSize: '0.875rem' }}>
              <Typography variant="body2">
                <strong>Path:</strong> {currentNode.path}
              </Typography>
              <Typography variant="body2">
                <strong>Level:</strong> {currentNode.level}
              </Typography>
              {currentNode.parentNodeId && (
                <Typography variant="body2">
                  <strong>Parent ID:</strong> {currentNode.parentNodeId}
                </Typography>
              )}
              <Typography variant="body2">
                <strong>Children:</strong> {currentNode.hasChildren ? 'Yes' : 'No'}
              </Typography>
            </Box>
          </Box>

          {/* Available Actions */}
          {selectedNode?.availableActions && selectedNode.availableActions.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Available Actions
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {selectedNode.availableActions.map((action, index) => (
                  <Chip
                    key={index}
                    label={action}
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            </Box>
          )}


          {/*// =============================================================================================*/}
          {/*// ==== Dodane w Krok 2 >>>>*/}
 
          {/* =============================================================================================
          ==== WERSJA KAFELKOWA (BACKUP) >>>>
          {selectedNodeForms.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Czynności
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                {selectedNodeForms.map((form) => (
                  <Card
                    key={form.imObjectId}
                    sx={{
                      minWidth: 120,
                      maxWidth: 200,
                      backgroundColor: form.symbolBackColor,
                      color: form.symbolTextColor,
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.8,
                        transform: 'scale(1.02)',
                      },
                      transition: 'all 0.2s',
                    }}
                    onClick={() => alert(`Formularz: ${form.name}`)}
                  >
                    <CardContent sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                        {form.symbol}
                      </Typography>
                      <Typography variant="h6" sx={{ mt: 1, fontSize: '0.9rem' }}>
                        {form.name}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1, fontSize: '0.75rem', opacity: 0.8 }}>
                        {form.description}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
              
              {formsLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </Box>
          )}
          <<<< WERSJA KAFELKOWA (BACKUP) ====
          ============================================================================================= */}
                      
        {/* ✅ NOWA WERSJA - LISTA JAK W ISTNIEJĄCEJ APLIKACJI */}
        {selectedNodeForms.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Czynności
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
              {selectedNodeForms.map((form) => (
                <Box
                  key={form.formTemplateId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    transition: 'background-color 0.2s',
                  }}
                  onClick={() => navigate(`/forms/${form.formTemplateId}?nodeId=${nodeId}`)}
                >
                  {/* Symbol w kolorowym kółku */}
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      backgroundColor: form.symbolBackColor,
                      color: form.symbolTextColor,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '1.2rem',
                      mr: 2,
                      flexShrink: 0,
                    }}
                  >
                    {form.symbol}
                  </Box>
                  
                  {/* Name i description */}
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500, lineHeight: 1.3 }}>
                      {form.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                      {form.description}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
            
            {formsLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Box>
        )}


        {/*// <<<< Dodane w Krok 2 ====*/}
        {/*// =============================================================================================*/}
 



        </CardContent>

        <CardActions sx={{ justifyContent: 'space-between' }}>
          <Box>
            <Button
              startIcon={<AssignmentIcon />}
              variant="outlined"
              sx={{ mr: 1 }}
              onClick={() => navigate(`/forms?nodeId=${currentNode.nodeId}`)}
            >
              Forms
            </Button>
            <Button
              startIcon={<AssessmentIcon />}
              variant="outlined"
              onClick={() => navigate(`/reports?nodeId=${currentNode.nodeId}`)}
            >
              Reports
            </Button>
          </Box>
          <Tooltip title="Show object details">
            <IconButton 
              size="small"
              onClick={() => {
                // TODO: Show detailed object configuration
                console.log('Show object details:', currentNode);
              }}
            >
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </CardActions>
      </Card>
    );
  };

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      {renderBreadcrumbs()}
      
      <Box sx={{ display: 'flex', gap: 3, flexGrow: 1, overflow: 'hidden' }}>
        {/* Left Panel - Object Hierarchy */}
       <Box sx={{ width: '300px', minWidth: '300px' }}>
          <Paper 
            sx={{ 
              height: '100%', 
              p: 2, 
              display: 'flex', 
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            <Typography variant="h6" gutterBottom>
              Object Hierarchy
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
              <ObjectHierarchy 
                onNodeSelect={handleNodeSelect}
                showSearch={true}
                showActions={true}
              />
            </Box>
          </Paper>
        </Box>

        {/* Right Panel - Object Details */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
    {loading && !currentNode && (
      <Alert severity="info" sx={{ mb: 2 }}>
        Loading object details...
      </Alert>
    )}
    
    {renderNodeDetail()}
  </Box>
</Box>
      
    </Box>
  );
};

export default ObjectView;